/**
 * TypeScript Interfaces for AI Avatar Generation Feature
 *
 * Defines types for avatar folders, generated images, and prompt chat sessions.
 */

import type { Id } from '../../convex/_generated/dataModel';
import type { ReactNode } from 'react';

// ============================================================================
// Core Types
// ============================================================================

/**
 * Aspect ratio options for avatar generation
 */
export type TAspectRatio = '1:1' | '9:16' | '16:9' | '4:5';

/**
 * Avatar Folder with computed properties
 */
export interface IAvatarFolderWithCount {
  _id: Id<'avatarFolders'>;
  _creationTime: number;
  userId: Id<'users'>;
  title: string;
  description?: string;
  isDeleted: boolean;
  createdAt: number;
  updatedAt: number;
  imageCount: number;
  thumbnailUrl?: string;
}

/**
 * Generated Avatar with resolved URLs
 */
export interface IGeneratedAvatarWithUrl {
  _id: Id<'generatedAvatars'>;
  _creationTime: number;
  userId: Id<'users'>;
  folderId: Id<'avatarFolders'>;
  referencePersonId?: Id<'referencePersons'>;
  storageId: Id<'_storage'>;
  prompt?: string;
  aspectRatio: string;
  source?: 'generated' | 'uploaded';
  isReadyToUse: boolean;
  isDefault: boolean;
  isDeleted: boolean;
  createdAt: number;
  imageUrl: string;
  referencePersonName?: string;
}

/**
 * Generated image from avatar generation
 */
export interface IGeneratedImage {
  storageId: Id<'_storage'>;
  url: string;
  aspectRatio?: TAspectRatio;
}

// ============================================================================
// Component Props Types
// ============================================================================

/**
 * Save Avatar Modal Props - Redesigned modal for saving generated avatars
 */
export interface ISaveAvatarModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => Promise<void>;
  isSaving: boolean;
  selectedImages: IGeneratedImage[];
  folders: IAvatarFolderWithCount[];
  selectedFolderId: Id<'avatarFolders'> | null;
  onFolderChange: (folderId: Id<'avatarFolders'> | null) => void;
  newFolderTitle: string;
  onNewFolderTitleChange: (title: string) => void;
  isDefault?: boolean;
  onDefaultChange?: (isDefault: boolean) => void;
  referencePersonName?: string;
}

/**
 * Step Badge Props - Orange numbered circle for section headers
 */
export interface IStepBadgeProps {
  step: 1 | 2 | 3;
}

/**
 * Section Header Props - Step badge with label and optional right element
 */
export interface ISectionHeaderProps {
  step: 1 | 2 | 3;
  label: string;
  rightElement?: ReactNode;
}

// ============================================================================
// Alt Layout Component Props (Avatar Generate Page Redesign)
// ============================================================================

/**
 * Reference person option for dropdown
 */
export interface IReferencePersonOption {
  id: Id<'referencePersons'>;
  name: string;
  imageUrl?: string;
}

/**
 * Reference Card Props - Compact person selection card (200px fixed width)
 */
export interface IReferenceCardProps {
  selectedPersonId: Id<'referencePersons'> | null;
  onPersonChange: (id: Id<'referencePersons'>) => void;
  referencePersons: IReferencePersonOption[];
  isLoading?: boolean;
}

/**
 * Prompt Card Props - Main input area with controls
 */
export interface IPromptCardProps {
  prompt: string;
  onPromptChange: (value: string) => void;
  selectedRatio: TAspectRatio;
  onRatioChange: (ratio: TAspectRatio) => void;
  imageCount: 1 | 2 | 3 | 4;
  onImageCountChange: (count: 1 | 2 | 3 | 4) => void;
  attachedImage: IGeneratedImage | null;
  onRemoveAttachment: () => void;
  onImprove: () => void;
  onGenerate: () => void;
  isGenerating: boolean;
  isImproving?: boolean;
  disabled: boolean;
}

/**
 * Images Grid Props - Container with header and grid of images
 */
export interface IImagesGridProps {
  images: IGeneratedImage[];
  selectedIds: Set<string>;
  onToggleSelect: (storageId: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onView: (image: IGeneratedImage) => void;
  onDownload: (image: IGeneratedImage) => void;
  onEdit: (image: IGeneratedImage) => void;
  onDelete: (storageId: string) => void;
  onBulkDelete: () => void;
  onBulkDownload: () => void;
  onSave: () => void;
  isGenerating?: boolean;
}

/**
 * Image Card Props - Individual image with overlay actions
 */
export interface IImageCardProps {
  image: IGeneratedImage;
  isSelected: boolean;
  onToggleSelect: () => void;
  onView: () => void;
  onDownload: () => void;
  onEdit: () => void;
  onDelete: () => void;
  aspectRatio?: TAspectRatio;
}

/**
 * Image View Modal Props - Fullscreen image viewer
 */
export interface IImageViewModalProps {
  image: IGeneratedImage | null;
  isOpen: boolean;
  onClose: () => void;
  onDownload: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

// ============================================================================
// Constants
// ============================================================================

export const AVATAR_CONSTANTS = {
  MAX_FOLDER_TITLE_LENGTH: 50,
  MAX_FOLDER_DESCRIPTION_LENGTH: 200,
  MAX_PROMPT_LENGTH: 2000,
  MAX_IMAGES_PER_GENERATION: 4,
  DEFAULT_ASPECT_RATIO: '9:16',
  DEFAULT_IMAGE_COUNT: 1,
} as const;

export const AVATAR_UPLOAD_CONSTANTS = {
  MAX_FILE_SIZE: 20 * 1024 * 1024, // 20MB
  ACCEPTED_TYPES: ['image/jpeg', 'image/png', 'image/webp'] as const,
  ACCEPTED_EXTENSIONS: '.jpg,.jpeg,.png,.webp',
} as const;
